import * as THREE from 'three';

/**
 * Procedurally generated PBR textures (HTML canvas — no external/AI image tool).
 * Each returns a colour `map` + a grayscale `bump` (height) so surfaces read as
 * rough rock / grained wood / veined marble rather than flat colour.
 *
 * These are tinted per-material via `material.color`. Swap for real baked maps
 * later with zero wiring changes (`materials.createBaked(...)`).
 */

export interface TextureSet {
  map: THREE.CanvasTexture;
  bump: THREE.CanvasTexture;
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

function hash2(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(seed | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

function valueNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi, seed);
  const b = hash2(xi + 1, yi, seed);
  const c = hash2(xi, yi + 1, seed);
  const d = hash2(xi + 1, yi + 1, seed);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}

function fbm(x: number, y: number, seed: number, octaves = 5): number {
  let value = 0;
  let amp = 0.5;
  let freq = 1;
  for (let i = 0; i < octaves; i += 1) {
    value += amp * valueNoise(x * freq, y * freq, seed + i);
    freq *= 2;
    amp *= 0.5;
  }
  return value;
}

function makeCanvas(size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  return canvas;
}

function toTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

/** Rough, mottled stone surface (white-biased so it tints to cream / terracotta). */
export function createStoneTextures(size = 512): TextureSet {
  const map = makeCanvas(size);
  const bump = makeCanvas(size);
  const mctx = map.getContext('2d');
  const bctx = bump.getContext('2d');
  if (!mctx || !bctx) throw new Error('2D context unavailable for stone textures');
  const mImg = mctx.createImageData(size, size);
  const bImg = bctx.createImageData(size, size);

  const scale = 5;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = (x / size) * scale;
      const v = (y / size) * scale;
      const n = fbm(u, v, 11, 5);
      const speck = hash2(x, y, 7) > 0.985 ? -0.32 : 0;
      const bright = clamp01(0.66 + (n - 0.5) * 0.7 + speck);
      const h = clamp01(fbm(u, v, 23, 5) * 0.7 + fbm(u * 3, v * 3, 31, 4) * 0.3);

      const idx = (y * size + x) * 4;
      const c = Math.round(bright * 255);
      mImg.data[idx] = c;
      mImg.data[idx + 1] = c;
      mImg.data[idx + 2] = c;
      mImg.data[idx + 3] = 255;
      const hc = Math.round(h * 255);
      bImg.data[idx] = hc;
      bImg.data[idx + 1] = hc;
      bImg.data[idx + 2] = hc;
      bImg.data[idx + 3] = 255;
    }
  }
  mctx.putImageData(mImg, 0, 0);
  bctx.putImageData(bImg, 0, 0);
  return { map: toTexture(map), bump: toTexture(bump) };
}

/**
 * Offset-coursed masonry blocks with mortar joints and per-brick tonal variation
 * (white-biased so it tints to terracotta) — for the arch voussoirs. Replaces the
 * plain mottled `createStoneTextures` look there: the reference photo's wall reads
 * as distinct rectangular brick coursing, not a uniform speckled surface.
 */
export function createBrickTextures(size = 512): TextureSet {
  const map = makeCanvas(size);
  const bump = makeCanvas(size);
  const mctx = map.getContext('2d');
  const bctx = bump.getContext('2d');
  if (!mctx || !bctx) throw new Error('2D context unavailable for brick textures');
  const mImg = mctx.createImageData(size, size);
  const bImg = bctx.createImageData(size, size);

  const rows = 6;
  const brickH = size / rows;
  const brickW = brickH * 2.2;
  const mortar = brickH * 0.09;

  for (let y = 0; y < size; y += 1) {
    const row = Math.floor(y / brickH);
    const rowOffset = (row % 2) * (brickW / 2); // stagger alternating rows
    const by = y - row * brickH;
    const nearRowEdge = by < mortar || by > brickH - mortar;

    for (let x = 0; x < size; x += 1) {
      const xs = x + rowOffset;
      const col = Math.floor(xs / brickW);
      const bx = xs - col * brickW;
      const isMortar = nearRowEdge || bx < mortar || bx > brickW - mortar;

      const brickTone = hash2(col, row, 41); // per-brick colour variation
      const fine = fbm(x * 0.05, y * 0.05, 11, 4); // fine surface grain within each brick

      let bright: number;
      let height: number;
      if (isMortar) {
        bright = clamp01(0.32 + (fbm(x * 0.2, y * 0.2, 71, 2) - 0.5) * 0.08);
        height = 0.2;
      } else {
        bright = clamp01(0.6 + (brickTone - 0.5) * 0.4 + (fine - 0.5) * 0.18);
        height = clamp01(0.65 + (fine - 0.5) * 0.35);
      }

      const idx = (y * size + x) * 4;
      const c = Math.round(bright * 255);
      mImg.data[idx] = c;
      mImg.data[idx + 1] = c;
      mImg.data[idx + 2] = c;
      mImg.data[idx + 3] = 255;
      const hc = Math.round(height * 255);
      bImg.data[idx] = hc;
      bImg.data[idx + 1] = hc;
      bImg.data[idx + 2] = hc;
      bImg.data[idx + 3] = 255;
    }
  }
  mctx.putImageData(mImg, 0, 0);
  bctx.putImageData(bImg, 0, 0);
  return { map: toTexture(map), bump: toTexture(bump) };
}

/**
 * Rough brown sand / dry-dirt ground — warm mid-brown with darker grain specks and a
 * subtle large-scale tonal mottle, plus a bumpy height map. Sits UNDER the animated
 * grass so the ground reads as real earth showing between blades (2026-07-11, user
 * request), instead of a flat hidden plane. White-ish tinting is NOT used — the colour
 * is baked into the map so it needs no `material.color` tint.
 */
export function createSandTextures(size = 512): TextureSet {
  const map = makeCanvas(size);
  const bump = makeCanvas(size);
  const mctx = map.getContext('2d');
  const bctx = bump.getContext('2d');
  if (!mctx || !bctx) throw new Error('2D context unavailable for sand textures');
  const mImg = mctx.createImageData(size, size);
  const bImg = bctx.createImageData(size, size);

  const base = [0x8f, 0x6b, 0x43]; // warm mid-brown earth
  const dark = [0x5c, 0x40, 0x26]; // damp/shadowed grain
  const lightSpeck = [0xc2, 0xa2, 0x72]; // dry sand highlight grains
  const scale = 7;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = (x / size) * scale;
      const v = (y / size) * scale;
      const macro = fbm(u * 0.5, v * 0.5, 17, 4); // large tonal patches
      const grain = fbm(u * 6, v * 6, 41, 4); // fine sandy grain
      const r = hash2(x, y, 13);
      // Base → dark by grain, with occasional bright dry-sand specks.
      let t = clamp01(0.5 + (macro - 0.5) * 0.8 + (grain - 0.5) * 0.5);
      const idx = (y * size + x) * 4;
      let cr = lerp(dark[0]!, base[0]!, t);
      let cg = lerp(dark[1]!, base[1]!, t);
      let cb = lerp(dark[2]!, base[2]!, t);
      if (r > 0.86) {
        const s = (r - 0.86) / 0.14;
        cr = lerp(cr, lightSpeck[0]!, s);
        cg = lerp(cg, lightSpeck[1]!, s);
        cb = lerp(cb, lightSpeck[2]!, s);
      }
      mImg.data[idx] = Math.round(cr);
      mImg.data[idx + 1] = Math.round(cg);
      mImg.data[idx + 2] = Math.round(cb);
      mImg.data[idx + 3] = 255;
      const h = clamp01(0.45 + (grain - 0.5) * 0.6 + (macro - 0.5) * 0.3);
      const hc = Math.round(h * 255);
      bImg.data[idx] = hc;
      bImg.data[idx + 1] = hc;
      bImg.data[idx + 2] = hc;
      bImg.data[idx + 3] = 255;
    }
  }
  mctx.putImageData(mImg, 0, 0);
  bctx.putImageData(bImg, 0, 0);
  return { map: toTexture(map), bump: toTexture(bump) };
}

/** Vertical-grain wood with plank seams (for the doors). */
export function createWoodTextures(size = 512): TextureSet {
  const map = makeCanvas(size);
  const bump = makeCanvas(size);
  const mctx = map.getContext('2d');
  const bctx = bump.getContext('2d');
  if (!mctx || !bctx) throw new Error('2D context unavailable for wood textures');
  const mImg = mctx.createImageData(size, size);
  const bImg = bctx.createImageData(size, size);

  const dark = [0x3a, 0x22, 0x12];
  const light = [0x8a, 0x55, 0x30];
  const planks = 4;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = x / size;
      const v = y / size;
      const warp = fbm(u * 3, v * 10, 5, 4);
      const grain = 0.5 + 0.5 * Math.sin(u * planks * Math.PI * 2 + warp * 5);
      const fine = (fbm(u * 40, v * 120, 9, 3) - 0.5) * 0.25;
      const seamDist = Math.abs(((u * planks) % 1) - 0.5) * 2;
      const seam = seamDist > 0.92 ? -0.42 : 0;
      const t = clamp01(0.35 + grain * 0.4 + fine + seam);

      const idx = (y * size + x) * 4;
      mImg.data[idx] = Math.round(lerp(dark[0]!, light[0]!, t));
      mImg.data[idx + 1] = Math.round(lerp(dark[1]!, light[1]!, t));
      mImg.data[idx + 2] = Math.round(lerp(dark[2]!, light[2]!, t));
      mImg.data[idx + 3] = 255;
      const h = clamp01(0.5 + (grain - 0.5) * 0.5 + fine + seam * 0.6);
      const hc = Math.round(h * 255);
      bImg.data[idx] = hc;
      bImg.data[idx + 1] = hc;
      bImg.data[idx + 2] = hc;
      bImg.data[idx + 3] = 255;
    }
  }
  mctx.putImageData(mImg, 0, 0);
  bctx.putImageData(bImg, 0, 0);
  return { map: toTexture(map), bump: toTexture(bump) };
}

/**
 * Seamless, tiling water normal map for the three.js `Water` shader. Height is a
 * sum of sine waves with INTEGER tile-frequencies (so it wraps perfectly), and the
 * RGB normal is derived by central differences. Kept low-amplitude for a gently
 * rippled — not choppy — surface.
 */
export function createWaterNormals(size = 256): THREE.CanvasTexture {
  const canvas = makeCanvas(size);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable for water normals');
  const img = ctx.createImageData(size, size);

  const waves = [
    { fx: 1, fy: 2, a: 1.0, p: 0.0 },
    { fx: 3, fy: 1, a: 0.5, p: 1.3 },
    { fx: 2, fy: 4, a: 0.32, p: 2.1 },
    { fx: 5, fy: 3, a: 0.2, p: 0.7 },
  ];
  const height = (u: number, v: number): number => {
    let h = 0;
    for (const w of waves) h += w.a * Math.sin(2 * Math.PI * (w.fx * u + w.fy * v) + w.p);
    return h;
  };

  const eps = 1 / size;
  const strength = 0.35;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = x / size;
      const v = y / size;
      const dx = (height(u - eps, v) - height(u + eps, v)) * strength;
      const dy = (height(u, v - eps) - height(u, v + eps)) * strength;
      let nx = dx;
      let ny = dy;
      let nz = 1;
      const inv = 1 / Math.hypot(nx, ny, nz);
      nx *= inv;
      ny *= inv;
      nz *= inv;

      const idx = (y * size + x) * 4;
      img.data[idx] = Math.round((nx * 0.5 + 0.5) * 255);
      img.data[idx + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      img.data[idx + 2] = Math.round((nz * 0.5 + 0.5) * 255);
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const texture = toTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace; // normal data is linear
  return texture;
}

/** Large pale-cream flagstone paving — square-grid slabs with visible grout lines, for
 *  the stage/platform stones (`Stage_Stone_S_*`). Deliberately distinct from BOTH the
 *  arch's small offset-coursed terracotta brick (`createBrickTextures`) and the
 *  pillar's veined marble (`createMarbleTextures`, below) — a straight grid of much
 *  bigger slabs reads as "floor paving", not "wall masonry" or "polished trim"
 *  (2026-07-05, user: stone/pillar/stage should look visibly different from each
 *  other, not share the same underlying texture). */
export function createFlagstoneTextures(size = 512): TextureSet {
  const map = makeCanvas(size);
  const bump = makeCanvas(size);
  const mctx = map.getContext('2d');
  const bctx = bump.getContext('2d');
  if (!mctx || !bctx) throw new Error('2D context unavailable for flagstone textures');
  const mImg = mctx.createImageData(size, size);
  const bImg = bctx.createImageData(size, size);

  const cols = 4;
  const slab = size / cols;
  const grout = slab * 0.05;

  for (let y = 0; y < size; y += 1) {
    const row = Math.floor(y / slab);
    const sy = y - row * slab;
    const nearRowEdge = sy < grout || sy > slab - grout;

    for (let x = 0; x < size; x += 1) {
      const col = Math.floor(x / slab);
      const sx = x - col * slab;
      const isGrout = nearRowEdge || sx < grout || sx > slab - grout;

      const slabTone = hash2(col, row, 53); // per-slab tonal variation
      const fine = fbm(x * 0.04, y * 0.04, 29, 4); // fine surface grain within each slab

      let bright: number;
      let height: number;
      if (isGrout) {
        bright = clamp01(0.55 + (fbm(x * 0.3, y * 0.3, 61, 2) - 0.5) * 0.06);
        height = 0.25;
      } else {
        bright = clamp01(0.86 + (slabTone - 0.5) * 0.12 + (fine - 0.5) * 0.08);
        height = clamp01(0.6 + (fine - 0.5) * 0.25);
      }

      // Cream/pale-limestone tint (close family to the pillar's marble, but flatter —
      // no veining — so it still reads as a distinct, simpler paving material).
      const idx = (y * size + x) * 4;
      mImg.data[idx] = Math.round(bright * 0xf0);
      mImg.data[idx + 1] = Math.round(bright * 0xe6);
      mImg.data[idx + 2] = Math.round(bright * 0xd2);
      mImg.data[idx + 3] = 255;
      const hc = Math.round(height * 255);
      bImg.data[idx] = hc;
      bImg.data[idx + 1] = hc;
      bImg.data[idx + 2] = hc;
      bImg.data[idx + 3] = 255;
    }
  }
  mctx.putImageData(mImg, 0, 0);
  bctx.putImageData(bImg, 0, 0);
  return { map: toTexture(map), bump: toTexture(bump) };
}

/** Smooth cream limestone — faint speckling, only the barest hint of veining (for the
 *  pillars/stage/base). Tuned to sit close to `base`, not classic busy marble — the
 *  reference photo's trim reads as a uniform ivory stone, not heavily veined. */
export function createMarbleTextures(size = 512): TextureSet {
  const map = makeCanvas(size);
  const bump = makeCanvas(size);
  const mctx = map.getContext('2d');
  const bctx = bump.getContext('2d');
  if (!mctx || !bctx) throw new Error('2D context unavailable for marble textures');
  const mImg = mctx.createImageData(size, size);
  const bImg = bctx.createImageData(size, size);

  const base = [0xec, 0xe3, 0xd1];
  const vein = [0xda, 0xcf, 0xb6]; // close to base — a hint, not a busy vein pattern
  const scale = 1.3; // sparser, larger-scale pattern than a dense marble slab
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = (x / size) * scale;
      const v = (y / size) * scale;
      const turb = fbm(u, v, 3, 5);
      const s = Math.sin((u * 1.6 + v * 0.8 + turb * 3.2) * Math.PI);
      const m = clamp01(1 - Math.min(1, Math.abs(s) * 3)) * 0.45; // halved vein opacity
      const grain = (fbm(u * 8, v * 8, 17, 3) - 0.5) * 0.06;

      const idx = (y * size + x) * 4;
      mImg.data[idx] = Math.round(clamp01(lerp(base[0]!, vein[0]!, m) / 255 + grain) * 255);
      mImg.data[idx + 1] = Math.round(clamp01(lerp(base[1]!, vein[1]!, m) / 255 + grain) * 255);
      mImg.data[idx + 2] = Math.round(clamp01(lerp(base[2]!, vein[2]!, m) / 255 + grain) * 255);
      mImg.data[idx + 3] = 255;
      const hc = Math.round(clamp01(0.5 + m * 0.3 + grain) * 255);
      bImg.data[idx] = hc;
      bImg.data[idx + 1] = hc;
      bImg.data[idx + 2] = hc;
      bImg.data[idx + 3] = 255;
    }
  }
  mctx.putImageData(mImg, 0, 0);
  bctx.putImageData(bImg, 0, 0);
  return { map: toTexture(map), bump: toTexture(bump) };
}
